<template>
    <div v-if="loaded" class="nevs-content">
        <div class="nevs-content-top-buttons">
            <NevsButton v-if="plan.editable" class="success" @click="saveClick()"><i class="fa-solid fa-floppy-disk"></i>
                {{ $LANG.Get('buttons.save') }}
            </NevsButton>&nbsp;
            <NevsButton v-if="canSend" class="primary" @click="sendClick()"><i class="fa-solid fa-paper-plane"></i>
                {{ plan.status === 'sent' ? $LANG.Get('buttons.resendPaymentPlan') : $LANG.Get('buttons.startPaymentPlan') }}
            </NevsButton>&nbsp;
            <NevsButton v-if="canCancel" class="error" @click="cancelClick()"><i class="fa-solid fa-ban"></i>
                {{ $LANG.Get('buttons.cancelPaymentPlan') }}
            </NevsButton>&nbsp;
            <NevsButton class="error" @click="backClick"><i class="fa-solid fa-arrow-left"></i>
                {{ $LANG.Get('buttons.back') }}
            </NevsButton>
        </div>

        <NevsCard class="plan-status-card">
            <div class="plan-status-row">
                <span :class="'plan-status plan-status-' + plan.status">{{ $LANG.Get('paymentPlanStatuses.' + plan.status) }}</span>
                <span v-if="plan.status !== 'draft'" class="plan-status-progress">
                    {{ plan.paid_count }} / {{ plan.months }} {{ $LANG.Get('labels.instalmentsCollected') }}
                </span>
                <span v-if="plan.start_date_display" class="plan-status-progress">
                    {{ $LANG.Get('fields.startDate') }}: {{ plan.start_date_display }}
                </span>
                <span v-if="plan.cancel_at_display" class="plan-status-progress">
                    {{ $LANG.Get('labels.chargesUntil') }}: {{ plan.cancel_at_display }}
                </span>
            </div>
            <p class="plan-status-hint">{{ $LANG.Get('paymentPlanHints.' + plan.status) }}</p>
            <div v-if="plan.error" class="plan-error">
                <i class="fa-solid fa-triangle-exclamation"></i> {{ plan.error }}
            </div>
            <div v-if="plan.payment_url && plan.status === 'sent'" class="plan-link">
                <span class="plan-link-label">{{ $LANG.Get('labels.paymentLink') }}</span>
                <a :href="plan.payment_url" target="_blank" rel="noopener">{{ plan.payment_url }}</a>
                <span v-if="plan.valid_until_display" class="plan-link-valid">
                    ({{ $LANG.Get('labels.validUntil') }} {{ plan.valid_until_display }})
                </span>
            </div>
        </NevsCard>

        <NevsCard class="plan-form">
            <div class="plan-form-grid">
                <NevsSelect v-model="plan.person_id" :ajax="'select/persons'" :error="validation.person_id"
                            :label="'*' + $LANG.Get('fields.person') + ':'"
                            :readonly="!plan.editable"></NevsSelect>
                <NevsNumberField v-model="plan.months" :decimal-places="0" :thousand-separator="''"
                                 :error="validation.months"
                                 :label="'*' + $LANG.Get('fields.numberOfMonths') + ':'"
                                 :readonly="!plan.editable"></NevsNumberField>
            </div>
            <NevsTextArea v-model="plan.note" :label="$LANG.Get('fields.note') + ':'"
                          :readonly="!plan.editable"></NevsTextArea>
        </NevsCard>

        <NevsCard class="plan-items-card">
            <h2>{{ $LANG.Get('labels.paymentPlanItems') }}</h2>
            <p class="plan-items-hint">{{ $LANG.Get('labels.paymentPlanItemsHint') }}</p>
            <NevsSelect v-if="plan.editable" v-model="selectedThing" :ajax="'select/things-no-wares'"
                        :error="validation.items"
                        :label="$LANG.Get('labels.selectItem')" class="item-select"></NevsSelect>
            <div class="items-container">
                <table v-show="plan.items.length !== 0" class="items-table">
                    <thead>
                    <tr class="items-table-header">
                        <td v-for="(header, key) in headers" :key="key" :style="{'width': header.width}">{{ header.text }}</td>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(item, key) in plan.items" :key="key">
                        <td class="items-table-buttons">
                            <NevsButton v-if="plan.editable" @click="removeItem(key)"><i class="fa-solid fa-trash"></i></NevsButton>
                        </td>
                        <td>
                            <NevsTextField v-model="item.description"
                                           :readonly="!plan.editable || item.product_id !== null"></NevsTextField>
                        </td>
                        <td>
                            <NevsSelect v-model="item.measurement_unit_id" :ajax="'select/measurement-units'"
                                        :readonly="!plan.editable || item.product_id !== null"></NevsSelect>
                        </td>
                        <td><NevsNumberField v-model="item.price" :readonly="!plan.editable"></NevsNumberField></td>
                        <td><NevsNumberField v-model="item.quantity" :decimal-places="3" :thousand-separator="''"
                                             :readonly="!plan.editable"></NevsNumberField></td>
                        <td v-if="$store.state.user.client.tax_system">
                            <NevsNumberField v-model="item.tax" :readonly="!plan.editable"></NevsNumberField>
                        </td>
                        <td><NevsNumberField v-model="item.discount" :readonly="!plan.editable"></NevsNumberField></td>
                        <td><NevsNumberField :model-value="lineTotal(item)" :readonly="true"></NevsNumberField></td>
                    </tr>
                    </tbody>
                </table>

                <table v-show="plan.items.length !== 0" class="summary-table">
                    <tbody>
                    <template v-if="$store.state.user.client.tax_system">
                        <tr v-for="(base, key) in taxBases" :key="key">
                            <td>{{ $LANG.Get('labels.taxBase') }} {{ base.base }} ({{ plan.currency }}):</td>
                            <td class="summary-value">{{ formatAmount(base.value) }}</td>
                        </tr>
                    </template>
                    <tr class="summary-strong">
                        <td>{{ $LANG.Get('fields.monthlyInstalment') }} ({{ plan.currency }}):</td>
                        <td class="summary-value">{{ formatAmount(monthlyAmount) }}</td>
                    </tr>
                    <tr>
                        <td>{{ $LANG.Get('fields.numberOfMonths') }}:</td>
                        <td class="summary-value">{{ plan.months }}</td>
                    </tr>
                    <tr class="summary-strong">
                        <td>{{ $LANG.Get('fields.totalAmount') }} ({{ plan.currency }}):</td>
                        <td class="summary-value">{{ formatAmount(monthlyAmount * (plan.months || 0)) }}</td>
                    </tr>
                    </tbody>
                </table>
                <div class="nevs-clear-float"></div>
            </div>
        </NevsCard>

        <NevsCard v-if="plan.instalments.length !== 0" class="plan-instalments-card">
            <h2>{{ $LANG.Get('labels.collectedInstalments') }}</h2>
            <NevsSimpleGrid :fields="instalmentFields" :records="plan.instalments" :clickable="false"
                            :empty-text="$LANG.Get('labels.noInstalments')">
                <template #cell-invoice_display="{ record }">
                    <a v-if="record.invoice_id" href="javascript:;" @click="openInvoice(record)">{{ record.invoice_display }}</a>
                    <span v-else class="plan-instalment-error">
                        <i class="fa-solid fa-triangle-exclamation"></i> {{ record.error || '-' }}
                    </span>
                </template>
            </NevsSimpleGrid>
        </NevsCard>
    </div>
</template>

<script>
import NevsCard from "@/components/nevs/NevsCard.vue";
import NevsButton from "@/components/nevs/NevsButton.vue";
import NevsSelect from "@/components/nevs/NevsSelect.vue";
import NevsTextField from "@/components/nevs/NevsTextField.vue";
import NevsTextArea from "@/components/nevs/NevsTextArea.vue";
import NevsNumberField from "@/components/nevs/NevsNumberField.vue";
import NevsSimpleGrid from "@/components/nevs/NevsSimpleGrid.vue";

export default {
    name: "EntityPaymentPlan",
    components: {NevsSimpleGrid, NevsNumberField, NevsTextArea, NevsTextField, NevsSelect, NevsButton, NevsCard},
    data() {
        return {
            loaded: false,
            mode: 'add',
            selectedThing: null,
            plan: {
                id: 0,
                person_id: null,
                person_name: '',
                months: 6,
                currency: 'EUR',
                status: 'draft',
                editable: true,
                paid_count: 0,
                start_date_display: null,
                payment_url: null,
                valid_until_display: null,
                cancel_at_display: null,
                note: '',
                error: '',
                items: [],
                instalments: []
            },
            validation: {
                person_id: null,
                months: null,
                items: null
            }
        }
    },
    computed: {
        canSend() {
            return this.plan.status === 'draft' || this.plan.status === 'sent';
        },
        canCancel() {
            return this.plan.id !== 0 && (this.plan.status === 'sent' || this.plan.status === 'active');
        },
        headers() {
            let headers = [
                {text: '', width: '40px'},
                {text: this.$LANG.Get('fields.description'), width: 'auto'},
                {text: this.$LANG.Get('fields.measurementUnit'), width: '140px'},
                {text: this.$LANG.Get('fields.price'), width: '110px'},
                {text: this.$LANG.Get('fields.quantity'), width: '90px'}
            ];
            if (this.$store.state.user.client.tax_system) {
                headers.push({text: this.$LANG.Get('fields.tax'), width: '80px'});
            }
            headers.push({text: this.$LANG.Get('fields.discount'), width: '90px'});
            headers.push({text: this.$LANG.Get('fields.total'), width: '110px'});
            return headers;
        },
        /**
         * The net amount per tax rate, exactly the grouping the server charges and invoices by - the tax is
         * rounded once per rate, not per line, so what is shown here is what Stripe will take.
         *
         * A club outside the tax system still gets one group, at rate 0, because the monthly amount is summed
         * from it; the groups themselves are only shown to clubs that charge tax.
         */
        taxBases() {
            let taxSystem = this.$store.state.user.client.tax_system;
            let bases = {};
            for (let item of this.plan.items) {
                let rate = taxSystem ? Number(item.tax) : 0;
                let key = String(rate);
                if (bases[key] === undefined) bases[key] = {base: rate, value: 0};
                bases[key].value = Math.round((bases[key].value + this.lineTotal(item)) * 100) / 100;
            }
            return Object.values(bases);
        },
        monthlyAmount() {
            let total = 0;
            for (let base of this.taxBases) {
                total += base.value + Math.round(base.value * base.base) / 100;
            }
            return Math.round(total * 100) / 100;
        },
        instalmentFields() {
            return [
                {name: 'number', label: this.$LANG.Get('fields.instalmentNumber')},
                {name: 'amount_display', label: this.$LANG.Get('fields.amount')},
                {name: 'paid_at_display', label: this.$LANG.Get('fields.paidAt')},
                {name: 'invoice_display', label: this.$LANG.Get('fields.invoice')}
            ];
        }
    },
    watch: {
        'selectedThing': function () {
            if (this.selectedThing !== null) this.addItem();
        }
    },
    methods: {
        /** One line's net amount after its own discount - the same expression the server uses. */
        lineTotal(item) {
            let gross = Math.round(Number(item.price) * 100) / 100 * Number(item.quantity);
            return Math.round(gross * (1 - (Number(item.discount) / 100)) * 100) / 100;
        },
        formatAmount(value) {
            return Number(value).toFixed(2).replace('.', ',');
        },
        removeItem(key) {
            this.plan.items.splice(key, 1);
        },
        addItem() {
            let vm = this;
            let defaultUnit = (this.$store.state.user.client.default_measurement_unit_id !== null)
                ? this.$store.state.user.client.default_measurement_unit_id : 1;

            if (this.selectedThing === -1) {
                this.plan.items.push({
                    description: '', details: '',
                    code_kpd: this.$store.state.user.client.default_kpd_code || '',
                    price: 0, quantity: 1,
                    tax: this.$store.state.user.client.tax_system ? this.$store.state.settings.DEFAULT_TAX : 0,
                    discount: 0, measurement_unit_id: defaultUnit,
                    product_id: null, membership_id: null, package_id: null
                });
                this.$nextTick(() => {
                    vm.selectedThing = null;
                });
                return;
            }

            let parts = String(this.selectedThing).split('_');
            let type = parts[0];
            let id = parts[1];
            let endpoints = {PRODUCT: 'products/', MEMBERSHIP: 'memberships/', PACKAGE: 'packages/'};
            let keys = {PRODUCT: 'product', MEMBERSHIP: 'membership', PACKAGE: 'package'};
            if (endpoints[type] === undefined) {
                this.selectedThing = null;
                return;
            }

            this.$API.APICall('get', endpoints[type] + id, {}, (data, success) => {
                if (!success) {
                    vm.$HELPERS.HandleServerError(vm, data);
                    return;
                }
                let thing = data[keys[type]];
                vm.plan.items.push({
                    description: thing.name,
                    details: '',
                    code_kpd: thing.code_kpd ? thing.code_kpd : (vm.$store.state.user.client.default_kpd_code || ''),
                    price: thing.price,
                    quantity: 1,
                    tax: vm.$store.state.user.client.tax_system ? thing.tax : 0,
                    discount: 0,
                    measurement_unit_id: (type === 'PRODUCT') ? thing.measurement_unit_id : defaultUnit,
                    product_id: (type === 'PRODUCT') ? thing.id : null,
                    membership_id: (type === 'MEMBERSHIP') ? thing.id : null,
                    package_id: (type === 'PACKAGE') ? thing.id : null
                });
                vm.selectedThing = null;
            }, false);
        },
        openInvoice(instalment) {
            this.$router.push('/invoices/' + instalment.invoice_id + '/edit');
        },
        backClick() {
            this.$router.push('/payment-plans');
        },
        saveClick(callback = null) {
            this.validation = {person_id: null, months: null, items: null};

            let method = (this.mode === 'add') ? 'post' : 'put';
            let action = (this.mode === 'add') ? 'payment-plans' : 'payment-plans/' + this.plan.id;
            let payload = {
                person_id: this.plan.person_id,
                months: parseInt(this.plan.months),
                note: this.plan.note,
                items: this.plan.items
            };

            let vm = this;
            this.$API.APICall(method, action, payload, (data, success) => {
                if (!success) {
                    if (data && (data.person_id || data.months || data.items)) {
                        vm.validation = {
                            person_id: data.person_id ? vm.$LANG.Get('validations.' + data.person_id) : null,
                            months: data.months ? vm.$LANG.Get('validations.' + data.months) : null,
                            items: data.items ? vm.$LANG.Get('validations.' + data.items) : null
                        };
                        return;
                    }
                    vm.$HELPERS.HandleServerError(vm, data);
                    return;
                }
                vm.applyPlan(data.payment_plan);
                vm.mode = 'edit';
                vm.$LOCAL_BUS.TriggerEvent('notification', {text: vm.$LANG.Get('alerts.recordSaved')});
                if (callback !== null) callback();
            });
        },
        sendClick() {
            let vm = this;
            // Save first: what is sent has to be what is on screen, and the amount the member is asked for is
            // computed on the server from the saved lines.
            this.saveClick(() => {
                vm.$LOCAL_BUS.TriggerEvent('popup', {
                    type: 'confirm',
                    text: vm.$LANG.Get('alerts.confirmSendPaymentPlan')
                        .split('%email%').join(vm.plan.person_email || '')
                        .split('%months%').join(vm.plan.months)
                        .split('%amount%').join(vm.formatAmount(vm.monthlyAmount) + ' ' + vm.plan.currency),
                    callback: (response) => {
                        if (!response) return;
                        vm.$API.APICall('post', 'payment-plans/send/' + vm.plan.id, {}, (data, success) => {
                            if (success) {
                                vm.applyPlan(data.payment_plan);
                                vm.$LOCAL_BUS.TriggerEvent('notification', {text: vm.$LANG.Get('alerts.paymentPlanSent')});
                            } else {
                                vm.showPlanError(data);
                            }
                        });
                    }
                });
            });
        },
        cancelClick() {
            let vm = this;
            this.$LOCAL_BUS.TriggerEvent('popup', {
                type: 'confirm', text: this.$LANG.Get('alerts.confirmCancelPaymentPlan'), callback: (response) => {
                    if (!response) return;
                    vm.$API.APICall('post', 'payment-plans/cancel/' + vm.plan.id, {}, (data, success) => {
                        if (success) {
                            vm.applyPlan(data.payment_plan);
                            vm.$LOCAL_BUS.TriggerEvent('notification', {text: vm.$LANG.Get('alerts.paymentPlanCancelled')});
                        } else {
                            vm.showPlanError(data);
                        }
                    });
                }
            });
        },
        /** Turns the server's refusal into something a human can act on, falling back to the generic handler. */
        showPlanError(data) {
            let known = [
                'no_official_email', 'no_items', 'nothing_to_charge', 'stripe_not_enabled',
                'payment_plans_not_enabled', 'stripe_not_configured', 'email_failed', 'link_failed', 'subscription_not_found',
                'plan_not_awaiting_payment', 'plan_already_started', 'plan_already_finished',
                'plan_not_editable', 'subscription_could_not_be_cancelled', 'stripe_unavailable'
            ];
            if (data && known.indexOf(data.error) !== -1) {
                this.$LOCAL_BUS.TriggerEvent('popup', {
                    type: 'alert', text: this.$LANG.Get('paymentPlanErrors.' + data.error)
                });
                return;
            }
            this.$HELPERS.HandleServerError(this, data);
        },
        applyPlan(plan) {
            this.plan = JSON.parse(JSON.stringify(plan));
            if (!Array.isArray(this.plan.items)) this.plan.items = [];
            if (!Array.isArray(this.plan.instalments)) this.plan.instalments = [];
        }
    },
    mounted() {
        this.$store.commit('selectMenu', 'finance');
        this.$store.commit('selectSubMenu', 'paymentPlans');

        this.mode = (this.$route.params.id === '0') ? 'add' : 'edit';

        let breadcrumbs = (title) => [
            {label: this.$LANG.Get('modules.finance'), link: null},
            {label: this.$LANG.Get('modules.paymentPlans'), link: '/payment-plans'},
            {label: title, link: null}
        ];

        if (this.mode === 'add') {
            this.$store.commit('setBreadcrumbs', breadcrumbs(this.$LANG.Get('buttons.addPaymentPlan')));
            this.loaded = true;
            return;
        }

        let vm = this;
        this.$API.APICall('get', 'payment-plans/' + this.$route.params.id, {}, (data, success) => {
            if (success && data.payment_plan !== null) {
                vm.applyPlan(data.payment_plan);
                vm.$store.commit('setBreadcrumbs', breadcrumbs(vm.plan.person_name));
                vm.loaded = true;
            } else {
                vm.$HELPERS.HandleServerError(vm, data);
            }
        });
    }
}
</script>

<style scoped lang="scss">
.plan-status-card, .plan-form, .plan-items-card, .plan-instalments-card {
    padding: 16px;
    margin-bottom: 15px;
}

.plan-status-row {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
}

.plan-status {
    display: inline-block;
    padding: 3px 12px;
    border-radius: 12px;
    font-weight: bold;
    background: #eeeeee;
    color: #555555;
}

.plan-status-active { background: #e2f0e2; color: #2c6e2c; }
.plan-status-completed { background: #e2eaf5; color: #2f5590; }
.plan-status-cancelled { background: #f3e2e2; color: #8a3232; }

.plan-status-progress {
    font-size: 0.9em;
    color: #666666;
}

.plan-status-hint {
    margin: 10px 0 0;
    font-size: 0.85em;
    color: #888888;
    line-height: 1.4;
}

.plan-error {
    margin-top: 10px;
    padding: 8px 12px;
    border-radius: 6px;
    background: #fdf3e2;
    color: #8a5a00;
    font-size: 0.9em;
}

.plan-link {
    margin-top: 10px;
    font-size: 0.85em;
    word-break: break-all;
}

.plan-link-label {
    color: #888888;
    margin-right: 6px;
}

.plan-link-valid {
    color: #888888;
    margin-left: 6px;
}

.plan-form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0 15px;
}

.plan-items-hint {
    margin: 0 0 12px;
    font-size: 0.85em;
    color: #888888;
    line-height: 1.4;
}

.item-select {
    max-width: 400px;
}

.items-container {
    margin-top: 10px;
}

.items-table {
    width: 100%;
    border-collapse: collapse;
}

.items-table-header td {
    font-weight: bold;
    padding-bottom: 6px;
    font-size: 0.9em;
}

.items-table-buttons {
    white-space: nowrap;
}

.summary-table {
    float: right;
    margin-top: 12px;
    border-collapse: collapse;

    td {
        padding: 4px 0 4px 20px;
    }
}

.summary-value {
    text-align: right;
    white-space: nowrap;
}

.summary-strong td {
    font-weight: bold;
}

.plan-instalment-error {
    color: #8a5a00;
}

@media (max-width: 700px) {
    .plan-form-grid {
        grid-template-columns: 1fr;
    }
}
</style>
